import round from 'lodash/round';
import uniq from 'lodash/uniq';
import moment from 'moment';
import React from 'react';
import Icon from 'src/components/Icon/Icon';
import { z } from 'zod';
import sortBy from 'lodash/sortBy';
import indexOf from 'lodash/indexOf';
import { cn } from 'src/domains/common/components/utils';
import { convertAmount, matchStakeFactor } from './BetTickerBox.helpers';

export const DEFAULT_HIGHLIGHT_COLOR = '#4200ff';
export const DEFAULT_HIGHLIGHT_COLOR_2 = '#ff0000';
export const DEFAULT_HIGHLIGHT_COLOR_3 = '#72e9c5';
export const DEFAULT_HIGHLIGHT_COLOR_4 = '#fd8bdf';
export const DEFAULT_HIGHLIGHT_COLOR_5 = '#ebef0b';
export const DEFAULT_HIGHLIGHT_COLOR_6 = '#4fba78';
export const DEFAULT_HIGHLIGHT_COLOR_7 = '#f2c95a';

// based off kafka-avro-schema betting_bets with needed optionals for splitting bets by legs and removing data
export const BetTypeSchema = z.object({
    body: z.object({
        bet: z.object({
            account: z
                .object({
                    id: z.string(),
                    type: z.string(),
                    name: z.string(),
                })
                .optional()
                .nullable(),
            cashOut: z.boolean().optional(),
            channel: z.string().optional(),
            comment: z.string().nullable(),
            country: z.string(),
            currency: z.string(),
            eachWay: z.boolean().optional(),
            freebet: z.boolean().nullable(),
            id: z.number(),
            legs: z.array(
                z.object({
                    competition: z
                        .object({
                            id: z.string(),
                            idv2: z.string().nullable(),
                            name: z.string(),
                            url: z.string(),
                        })
                        .optional()
                        .nullable(),
                    eachWayTerms: z.object({ places: z.number(), reduction: z.string() }).nullable().optional(),
                    event: z.object({
                        id: z.string(),
                        idv2: z.string(),
                        name: z.string(),
                        url: z.string(),
                    }),
                    eventCountry: z.array(z.string()),
                    id: z.string().nullable(),
                    inPlay: z.boolean(),
                    market: z
                        .object({
                            id: z.string(),
                            idv2: z.string(),
                            name: z.string(),
                            url: z.string(),
                        })
                        .optional()
                        .nullable(),
                    price: z
                        .object({
                            d: z.number(),
                            f: z.string(),
                        })
                        .optional()
                        .nullable(),
                    priceType: z.string(),
                    problems: z.array(
                        z.object({
                            resource: z.string(),
                            code: z.string(),
                            fieldName: z.string().nullable(),
                            debugDetails: z.string().nullable(),
                            details: z.string().nullable(),
                            pointer: z.string().nullable(),
                        })
                    ),
                    result: z.any().nullable(),
                    selection: z.object({
                        id: z.string(),
                        idv2: z.string(),
                        name: z.string(),
                        url: z.string().nullable(),
                    }),
                    spPrice: z
                        .object({
                            d: z.number(),
                            f: z.string(),
                        })
                        .optional()
                        .nullable(),
                    sport: z
                        .object({
                            id: z.string(),
                            idv2: z.string().nullable(),
                            name: z.string(),
                            url: z.string(),
                        })
                        .optional()
                        .nullable(),
                    termsWithBet: z.boolean().nullable(),
                    type: z.string(),
                })
            ),
            maxBet: z.number().optional().nullable(),
            maxBetPrecise: z.string().nullable(),
            minPotentialReturns: z.number().optional().nullable(),
            minPotentialReturnsPrecise: z.string().nullable(),
            newTrading: z.boolean(),
            numLines: z.number(),
            operatorPayout: z.number().nullable(),
            operatorPayoutPrecise: z.string().nullable(),
            operatorStakePerLine: z.number().nullable(),
            operatorStakePerLinePrecise: z.string(),
            payout: z.number().nullable().optional(),
            payoutPrecise: z.string().nullable(),
            placedAt: z.string().optional().nullable(),
            placedBy: z.any().nullable(),
            potentialReturns: z.number().nullable(),
            potentialReturnsPrecise: z.string().nullable(),
            remarks: z
                .array(
                    z.object({
                        resource: z.string(),
                        code: z.string(),
                        fieldName: z.string().nullable(),
                        debugDetails: z.string().nullable(),
                        details: z.string().nullable(),
                        pointer: z.string().nullable(),
                    })
                )
                .nullable(),
            settledAt: z.string().nullable(),
            settledBy: z.any().nullable(),
            stakeFactor: z.number().optional(),
            stakePerLine: z.number().optional(),
            stakePerLinePrecise: z.string().nullable(),
            status: z.string(),
            totalStake: z.number().optional(),
            totalStakePrecise: z.string().nullable(),
            type: z.string(),
        }),
    }),
    header: z.object({
        clientRequestId: z.string(),
        id: z.string(),
        type: z.string(),
        universe: z.string(),
        when: z.string(),
        who: z.object({
            id: z.string(),
            name: z.string(),
            type: z.string(),
            universe: z.string(),
            ip: z.string(),
        }),
    }),
});

const RulesSchema = z.object({
    betsAbove: z.number().optional(),
    betType: z.string().optional(),
    channel: z.string().optional(),
    sport: z
        .array(
            z.object({
                id: z.string(),
                competitions: z.union([
                    z.boolean(),
                    z.array(
                        z.object({
                            id: z.string(),
                            name: z.string().optional(),
                        })
                    ),
                ]),
            })
        )
        .optional(),
    stakeFactor: z.number().optional(),
    stakeFactorCondition: z.string().optional(),
    betsType: z.array(z.string()).optional(),
    rejectedBets: z.boolean().optional(),
    freebet: z.boolean().optional(),
    cashOutBets: z.boolean().optional(),
});

export type RulesType = z.infer<typeof RulesSchema>;

type SuccessDataType = z.infer<typeof BetTypeSchema>;

export type BetType = [SuccessDataType][number];

export interface ConfigType {
    channelDesktop: boolean;
    channelMobile: boolean;
    channelQuickbet: boolean;
    channelTelebetting: boolean;
    highlightBetsAbove?: number;
    highlightMaxStakePercentage?: number;
    highlightStakeFactor?: number;
    highlightStakeFactorCondition?: string;
    desktopColour?: string;
    mobileColour?: string;
    quickbetColour?: string;
    telebettingColour?: string;
    highlightBetsAboveColour?: string;
    highlightMaxStakePercentageColour?: string;
    highlightStakeFactorColour?: string;
}

const reportedError = new Set();

function highlightStakeFactor(row: BetType, config: ConfigType, rules: RulesType): string | undefined {
    if (config.highlightStakeFactorCondition !== undefined && config.highlightStakeFactor !== undefined)
        return matchStakeFactor(
            row.body.bet.stakeFactor,
            config.highlightStakeFactorCondition,
            config.highlightStakeFactor
        )
            ? (config.highlightStakeFactorColour ?? DEFAULT_HIGHLIGHT_COLOR_2)
            : undefined;
    else if (row.body.bet.stakeFactor !== undefined)
        return matchStakeFactor(row.body.bet.stakeFactor, rules.stakeFactorCondition, rules.stakeFactor) ||
            row.body.bet.stakeFactor < 1
            ? (config.highlightStakeFactorColour ?? DEFAULT_HIGHLIGHT_COLOR_2)
            : undefined;
    return undefined;
}

const getChannelColour = (row: BetType, config: ConfigType): string | undefined => {
    if (config.channelDesktop === true && row.body.bet.channel === 'desktop') {
        return config.desktopColour ?? DEFAULT_HIGHLIGHT_COLOR_3;
    } else if (config.channelMobile === true && row.body.bet.channel === 'mobile') {
        return config.mobileColour ?? DEFAULT_HIGHLIGHT_COLOR_4;
    } else if (config.channelQuickbet === true && row.body.bet.channel === 'quickbet') {
        return config.quickbetColour ?? DEFAULT_HIGHLIGHT_COLOR_5;
    } else if (config.channelTelebetting === true && row.body.bet.channel === 'telebetting') {
        return config.telebettingColour ?? DEFAULT_HIGHLIGHT_COLOR_6;
    }
};

const ExampleColumns = z.array(
    z.object({
        id: z.string(),
        header: z.string(),
        value: z
            .function()
            .args(BetTypeSchema)
            .returns(z.union([z.string(), z.number(), z.unknown()])),
        width: z.number(),
    })
);

export type ColumnsType = z.infer<typeof ExampleColumns>;

export const createColumns = (config: ConfigType, rules: RulesType): ColumnsType => [
    {
        id: 'placedAt',
        header: 'Placed At',
        value: (row: BetType): React.JSX.Element => {
            const bgColour = getChannelColour(row, config);
            const time =
                row.body.bet.placedAt !== undefined && row.body.bet.placedAt !== null
                    ? moment(row.body.bet.placedAt).format('DD/MM/YYYY HH:mm:ss')
                    : undefined;
            return (
                <div
                    className={cn('flex h-6 items-center', typeof bgColour === 'string' && 'pl-1')}
                    style={typeof bgColour === 'string' ? { backgroundColor: bgColour } : undefined}
                    title={time}
                >
                    {time}
                </div>
            );
        },
        width: 125,
    },
    {
        id: 'settledAt',
        header: 'Settled At',
        value: (row: BetType): React.JSX.Element => {
            const bgColour = getChannelColour(row, config);
            const time =
                row.body.bet.settledAt === null
                    ? undefined
                    : moment(row.body.bet.settledAt).format('DD/MM/YYYY HH:mm:ss');
            return (
                <div
                    className={cn('flex h-6 items-center', typeof bgColour === 'string' && 'pl-1')}
                    style={typeof bgColour === 'string' ? { backgroundColor: bgColour } : undefined}
                    title={time}
                >
                    {time}
                </div>
            );
        },
        width: 125,
    },
    {
        id: 'payout',
        header: 'Payout',
        value: (row: BetType): React.JSX.Element | null => {
            if (row.body.bet.payout === undefined || row.body.bet.payout === null) {
                return null;
            }
            return (
                <div
                    className='flex h-6 items-center'
                    title={convertAmount(row.body.bet.payout) ?? ''}
                >
                    {row.body.bet.freebet === true && <Icon name='gift' />}
                    {convertAmount(row.body.bet.payout) ?? 0}
                </div>
            );
        },
        width: 110,
    },
    {
        id: 'customer',
        header: 'Customer',
        value: (row: BetType): React.JSX.Element | null =>
            row.body.bet.account !== undefined && row.body.bet.account !== null ? (
                <a
                    href={`/trading?*customers=${row.body.bet.account.id}&*customers.parameters={"tab": "customer"}`}
                    target='_blank'
                    rel='noopener noreferrer'
                    title={row.body.bet.account.name}
                >
                    {row.body.bet.account.name}
                </a>
            ) : null,
        width: 130,
    },
    {
        id: 'stakeFactor',
        header: 'SF',
        value: (row: BetType): React.JSX.Element | null => {
            const bgColour = highlightStakeFactor(row, config, rules);
            return row.body.bet.stakeFactor === undefined ? null : (
                <div
                    className={cn('flex h-6 items-center', typeof bgColour === 'string' && 'pl-1')}
                    style={typeof bgColour === 'string' ? { backgroundColor: bgColour } : undefined}
                    title={row.body.bet.stakeFactor.toString()}
                >
                    {row.body.bet.stakeFactor.toString()}
                </div>
            );
        },
        width: 40,
    },
    {
        id: 'inPlay',
        header: 'In-play',
        value: (row: BetType): string =>
            row.body.bet.legs.reduce((value, next) => value && next.inPlay, true) ? 'Y' : 'N',
        width: 55,
    },
    {
        id: 'sport',
        header: 'Sport',
        value: (row: BetType): string => {
            const sports = uniq(row.body.bet.legs.map((leg) => leg.sport !== undefined && leg.sport?.name))
                .filter((x) => x)
                .join(', ');

            return sports.length > 0 ? sports : '-';
        },
        width: 100,
    },
    {
        id: 'competition',
        header: 'Competition',
        value: (row: BetType): string => {
            const competitions = uniq(
                row.body.bet.legs.map((leg) => leg.competition !== undefined && leg.competition?.name)
            )
                .filter((x) => x)
                .join(', ');
            return competitions.length > 0 ? competitions : '-';
        },
        width: 180,
    },
    {
        id: 'event',
        header: 'Event',
        value: (row: BetType): React.JSX.Element => {
            const events = uniq(row.body.bet.legs.map((leg) => leg.event.name)).join(', ');
            return (
                <a
                    href={`/trading?*events=${row.body.bet.legs[0]?.event.id}`}
                    target='_blank'
                    rel='noopener noreferrer'
                    title={events}
                >
                    {events}
                </a>
            );
        },
        width: 250,
    },
    {
        id: 'market',
        header: 'Market',
        value: (row: BetType): string | React.JSX.Element => {
            let link;
            try {
                const eventId = row.body.bet.legs[0]?.event.id;
                const marketId = row.body.bet.legs[0]?.market?.id;

                link = `/trading?*events=${eventId}&*events.parameters={"tab":"markets","market":${marketId}}`;
            } catch (err) {
                const errorId = JSON.stringify(row.body.bet);

                if (!reportedError.has(errorId)) {
                    reportedError.add(errorId);
                    window.console.error({
                        kind: 'BetTicker Error1',
                        err: err,
                        data: row.body.bet,
                    });
                }
            }
            const markets = uniq(row.body.bet.legs.map((leg) => leg.market !== undefined && leg.market?.name))
                .filter((x) => x)
                .join(', ');
            return markets.length > 0 ? (
                <a
                    href={link}
                    target='_blank'
                    rel='noopener noreferrer'
                    title={markets}
                >
                    {markets}
                </a>
            ) : (
                '-'
            );
        },
        width: 130,
    },
    {
        id: 'selection',
        header: 'Selection',
        value: (row: BetType): React.JSX.Element => {
            let link;
            try {
                const type = row.body.bet.legs.length > 1 ? 'multiple' : 'single';
                const eventId = row.body.bet.legs[0]?.event.id;
                const marketId = row.body.bet.legs[0]?.market?.id;

                link = `/trading?tab=liabilities&*liabilities=${type}&*liabilities.parameters={"event":${eventId},"market":${marketId}}`;
            } catch (err) {
                const errorId = JSON.stringify(row.body.bet);

                if (!reportedError.has(errorId)) {
                    reportedError.add(errorId);
                    window.console.error({
                        kind: 'BetTicker Error2',
                        err: err,
                        data: row.body.bet,
                    });
                }
            }

            const selections = uniq(row.body.bet.legs.map((leg) => leg.selection.name)).join(', ');

            return (
                <a
                    href={link}
                    target='_blank'
                    rel='noopener noreferrer'
                    title={selections}
                >
                    {selections}
                </a>
            );
        },
        width: 130,
    },
    {
        id: 'price',
        header: 'Price',
        value: (row: BetType): string | null =>
            row.body.bet.legs[0]?.price === undefined || row.body.bet.legs[0].price === null
                ? null
                : row.body.bet.legs[0].price.f,
        width: 70,
    },
    {
        id: 'decimalPrice',
        header: 'Dec. Price',
        value: (row: BetType): string | null =>
            row.body.bet.legs[0]?.price === undefined || row.body.bet.legs[0].price === null
                ? null
                : round(row.body.bet.legs[0].price.d, 2).toString(),
        width: 90,
    },
    {
        id: 'priceType',
        header: 'Price Type',
        value: (row: BetType): string | undefined => row.body.bet.legs[0]?.priceType.toUpperCase(),
        width: 90,
    },
    {
        id: 'ew',
        header: 'EW',
        value: (row: BetType): string | null =>
            row.body.bet.eachWay === undefined ? null : row.body.bet.eachWay ? 'Y' : 'N',
        width: 30,
    },
    {
        id: 'type',
        header: 'Bet type',
        value: (row: BetType): string => row.body.bet.type,
        width: 70,
    },
    {
        id: 'stakePerLine',
        header: 'Stake Per Line',

        value: (row: BetType): React.JSX.Element | null => {
            if (row.body.bet.stakePerLine === undefined) {
                return null;
            }
            let bgColour = undefined;
            if (
                config.highlightBetsAbove !== undefined &&
                row.body.bet.stakePerLine > config.highlightBetsAbove * 100
            ) {
                bgColour = config.highlightBetsAboveColour ?? DEFAULT_HIGHLIGHT_COLOR;
            }

            return (
                <div
                    className={cn('flex h-6 items-center', typeof bgColour === 'string' && 'pl-1')}
                    style={typeof bgColour === 'string' ? { backgroundColor: bgColour } : undefined}
                    title={convertAmount(row.body.bet.stakePerLine) ?? ''}
                >
                    {row.body.bet.freebet === true && <Icon name='gift' />}
                    {convertAmount(row.body.bet.stakePerLine) ?? 0}
                </div>
            );
        },
        width: 90,
    },
    {
        id: 'totalStake',
        header: 'Total Stake',
        value: (row: BetType): React.JSX.Element | null => {
            if (row.body.bet.totalStake === undefined) {
                return null;
            }
            let bgColour = undefined;
            if (config.highlightBetsAbove !== undefined && row.body.bet.totalStake > config.highlightBetsAbove * 100) {
                bgColour = config.highlightBetsAboveColour ?? DEFAULT_HIGHLIGHT_COLOR;
            }

            return (
                <div
                    className={cn('flex h-6 items-center', typeof bgColour === 'string' && 'pl-1')}
                    style={typeof bgColour === 'string' ? { backgroundColor: bgColour } : undefined}
                    title={convertAmount(row.body.bet.totalStake) ?? ''}
                >
                    {row.body.bet.freebet === true && <Icon name='gift' />}
                    {convertAmount(row.body.bet.totalStake) ?? 0}
                </div>
            );
        },
        width: 75,
    },
    {
        id: 'maxLineStake',
        header: 'Max Line Stake',
        value: (row: BetType): string | null =>
            row.body.bet.maxBet === undefined || row.body.bet.maxBet === null
                ? null
                : convertAmount(row.body.bet.maxBet),
        width: 130,
    },
    {
        id: 'stakePerLine/maxAllowedBet',
        header: 'Stake per line/ Max allowed bet',
        value: (row: BetType): React.JSX.Element | null => {
            if (row.body.bet.stakePerLine === undefined) {
                return null;
            }
            let bgColour = undefined;
            if (
                config.highlightMaxStakePercentage !== undefined &&
                row.body.bet.maxBet !== undefined &&
                row.body.bet.maxBet !== null &&
                parseFloat(((100 * row.body.bet.stakePerLine) / row.body.bet.maxBet).toFixed(2)) >
                    config.highlightMaxStakePercentage
            ) {
                bgColour = config.highlightMaxStakePercentageColour ?? DEFAULT_HIGHLIGHT_COLOR_7;
            }
            return row.body.bet.maxBet === undefined || row.body.bet.maxBet === null ? null : (
                <div style={typeof bgColour === 'string' ? { backgroundColor: bgColour } : undefined}>
                    {((100 * row.body.bet.stakePerLine) / row.body.bet.maxBet).toFixed(2)}%
                </div>
            );
        },
        width: 110,
    },
    {
        id: 'channel',
        header: 'Channel',
        value: (row: BetType): React.JSX.Element => {
            const bgColour = getChannelColour(row, config);

            return (
                <div
                    className={cn('flex h-6 items-center', typeof bgColour === 'string' && 'pl-1')}
                    style={typeof bgColour === 'string' ? { backgroundColor: bgColour } : undefined}
                    title={row.body.bet.channel}
                >
                    {row.body.bet.channel}
                </div>
            );
        },
        width: 80,
    },
    {
        id: 'rejectReason',
        header: 'Reason',
        value: (row: BetType): string => {
            const order = [
                'maximum',
                'started',
                'suspended selection',
                'suspended market',
                'suspended event',
                'decrease',
                'increase',
                'bet-stake-too-high',
            ];

            return sortBy(row.body.bet.remarks, (remark) => {
                const testAgainst =
                    remark.code === 'suspended' ? `${remark.code} ${remark.resource}`.toLowerCase() : remark.code;
                return indexOf(order, testAgainst);
            })
                .map((remark) => `${remark.resource} ${remark.code}`)
                .join('\n');
        },
        width: 180,
    },
    {
        id: 'ip',
        header: 'IP',
        value: (row: BetType): string => row.header.who.ip,
        width: 320,
    },
];
